import { dateInputValue, isHistoricalRange, dashboardReferenceTime } from '../../utils/dashboard-range';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import LinePerTimeChart from '@components/charts/line_per_time_chart';
import MachineUserWeekTable from '@components/machine-user-week-table';
import HeartbeatTimelineChart from '@components/charts/heartbeat_timeline_chart';
import { AxiosInstanceLongTimeOut } from '@service/axios-instance';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  buildMachineUserAnalytics,
  buildUserOptions,
  MachineUserEvent,
  MachineUserHeartbeat,
  TimelineEntry,
  buildSelectedUserSet,
} from '../../utils/machine-user-analytics';

type ChecklistAnalytics = {
  total: number;
  completed: number;
  timeout: number;
  blocked: number;
  average_duration_minutes: number;
};

type MachineUser = {
  login?: string | number;
  name?: string;
  active?: boolean;
  type?: string;
  area?: string;
  machines?: string | string[];
};

type CanvasSeries = {
  type: 'line' | 'column';
  showInLegend?: boolean;
  name: string;
  xValueFormatString?: string;
  dataPoints: { x: Date; y: number; label?: string }[];
};

const requestConfig = { withCredentials: true };

function MetricCard({ title, value, detail, color = '#0f172a' }: { title: string; value: string | number; detail?: string; color?: string }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const displayColor = isDark && (color === '#0f172a' || color === '#475569') ? '#94a3b8' : color;

  return (
    <Paper
      elevation={5}
      sx={{
        p: 2.5,
        height: '100%',
        minHeight: 118,
        bgcolor: 'background.paper',
        backgroundImage: isDark
          ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f7fafc 100%)',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : undefined,
        borderTop: `4px solid ${displayColor} !important`,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ color: displayColor, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {detail && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {detail}
        </Typography>
      )}
    </Paper>
  );
}

function timelineLabel(entry: TimelineEntry): string {
  switch (entry.kind) {
    case 'login': return 'Entrada';
    case 'operating': return 'Operando';
    case 'break': return 'Pausa';
    case 'logout': return 'Saída';
    case 'checklist': return 'Checklist';
    case 'blocked': return 'Bloqueado';
    case 'updating': return 'Atualizando';
    default: return 'Estado desconhecido';
  }
}

function timelineColor(entry: TimelineEntry): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (entry.kind) {
    case 'login': return 'success';
    case 'operating': return 'info';
    case 'break': return 'warning';
    case 'blocked': return 'error';
    default: return 'default';
  }
}

function formatTimestamp(stamp?: string): string {
  if (!stamp) return '—';
  const date = new Date(stamp);
  if (Number.isNaN(date.getTime())) return stamp;
  return date.toLocaleString('pt-BR');
}

function formatHours(value: number): string {
  return `${value.toFixed(1).replace('.', ',')} h`;
}

function normalizeDashboardUser(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function MachineUsersDashboard() {
  const { operation, startDate, endDate, setStartDate, setEndDate } = useContext(GlobalContext);
  const [users, setUsers] = useState<MachineUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>(localStorage.getItem('selectedMachineDashboardUser') || '');
  const [heartbeats, setHeartbeats] = useState<MachineUserHeartbeat[]>([]);
  const [events, setEvents] = useState<MachineUserEvent[]>([]);
  const [checklistAnalytics, setChecklistAnalytics] = useState<ChecklistAnalytics | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventsPage, setEventsPage] = useState(1);
  const eventsPerPage = 10;
  const theme = useTheme();

  const userOptions = useMemo(() => buildUserOptions(users), [users]);
  const selectedUserRecord = users.find((user) => String(user.login ?? user.name ?? '') === selectedUser);
  const selectedUserAliases = useMemo(() => {
    if (!selectedUserRecord) return [];
    return [selectedUserRecord.login]
      .filter((value): value is string | number => value !== undefined && value !== null && String(value).length > 0);
  }, [selectedUserRecord]);

  const analytics = useMemo(() => buildMachineUserAnalytics({
    selectedUser,
    selectedUserAliases,
    heartbeats,
    events,
    rangeStart: startDate,
    rangeEnd: endDate,
  }), [selectedUser, selectedUserAliases, heartbeats, events, startDate, endDate]);

  const workHoursSeries: CanvasSeries[] = useMemo(() => ([
    {
      type: 'column',
      name: 'Horas',
      showInLegend: false,
      // Display the operational date without converting midnight to another day.
      dataPoints: analytics.dailyWorkHours.map(point => ({
        ...point,
        x: new Date(`${point.label}T12:00:00`),
        toolTipContent: `${point.label.split('-').reverse().join('/')} (Brasília): ${point.y.toLocaleString('pt-BR')} h`,
      })),
    }
  ]), [analytics.dailyWorkHours]);

  const selectedUserEvents = useMemo(() => {
    const selectedSet = new Set([selectedUser, ...selectedUserAliases].map(normalizeDashboardUser).filter(Boolean));
    const filteredEvents = events.filter((event) => selectedSet.has(normalizeDashboardUser(event.user)));
    const eventsWithTime = filteredEvents.map(evt => ({
      ...evt,
      timeValue: evt.stamp ? new Date(evt.stamp).getTime() : 0
    }));
    return eventsWithTime.sort((a, b) => b.timeValue - a.timeValue);
  }, [events, selectedUser, selectedUserAliases]);

  useEffect(() => {
    setEventsPage(1);
  }, [selectedUserEvents]);

  useEffect(() => {
    if (!operation) return;
    setLoadingUsers(true);
    setError(null);

    AxiosInstanceLongTimeOut.post(`/users-machine/get_users-machine?db=${operation}`, {}, requestConfig)
      .then((response) => {
        const loadedUsers = response.data || [];
        setUsers(loadedUsers);
        const options = buildUserOptions(loadedUsers);
        if (!selectedUser && options.length > 0) {
          setSelectedUser(options[0].value);
          localStorage.setItem('selectedMachineDashboardUser', options[0].value);
        }
      })
      .catch((err) => {
        console.error('machine users dashboard users load error', err);
        setError('Não foi possível carregar os usuários de máquina.');
      })
      .finally(() => setLoadingUsers(false));
  }, [operation]);

  useEffect(() => {
    if (!operation || !selectedUser) return;

    setLoadingTelemetry(true);
    setError(null);
    const baseRequest = {
      start_date: startDate,
      end_date: endDate,
      match_filters_dict: {},
    };

    const userFilterRaw = selectedUserRecord?.login ?? selectedUser;
    
    const heartbeatsRequestStr = {
      ...baseRequest,
      match_filters_dict: { user: String(userFilterRaw) },
    };
    
    const heartbeatsPromises = [
      AxiosInstanceLongTimeOut.post(`/heartbeats/get_heartbeats?db=${operation}`, heartbeatsRequestStr, requestConfig)
    ];

    if (userFilterRaw !== undefined && userFilterRaw !== null && !isNaN(Number(userFilterRaw)) && String(userFilterRaw).trim() !== '') {
      const heartbeatsRequestNum = {
        ...baseRequest,
        match_filters_dict: { user: Number(userFilterRaw) },
      };
      heartbeatsPromises.push(
        AxiosInstanceLongTimeOut.post(`/heartbeats/get_heartbeats?db=${operation}`, heartbeatsRequestNum, requestConfig)
      );
    }

    const userIds = Array.from(buildSelectedUserSet(selectedUser, selectedUserAliases));

    Promise.all([
      Promise.all(heartbeatsPromises).then(async (responses) => {
        // Fetch handovers/off readings only for machines used by this operator.
        const machineIds = Array.from(new Set(responses.flatMap((response) =>
          (response.data || []).map((heartbeat: MachineUserHeartbeat) => heartbeat.id)
        ))).filter((id) => id !== undefined && id !== null);
        if (!machineIds.length) return [];
        const response = await AxiosInstanceLongTimeOut.post(
          `/heartbeats/get_heartbeats?db=${operation}`,
          { ...baseRequest, match_filters_dict: { id: machineIds } },
          requestConfig,
        );
        return [response];
      }),
      AxiosInstanceLongTimeOut.post(`/events/get_events?db=${operation}`, baseRequest, requestConfig),
      ...userIds.map((uid: any) => AxiosInstanceLongTimeOut.post(`/checklists/get_checklist_analytics?db=${operation}`, {
        start_date: startDate,
        end_date: endDate,
        match_filters_dict: { user: String(uid) },
      }, requestConfig))
    ])
      .then(([heartbeatsResponsesArray, eventsResponse, ...checklistResps]) => {
        // Merge the results from both the string and number queries
        const rawHeartbeats = heartbeatsResponsesArray.reduce((acc: any[], resp: any) => acc.concat(resp.data || []), []);
        const rawEvents = eventsResponse.data || [];

        // Pre-parse dates to avoid creating millions of Date objects in loops
        const parsedHeartbeats = rawHeartbeats.map((hb: any) => ({
          ...hb,
          timeValue: hb.stamp ? new Date(hb.stamp).getTime() : 0
        }));

        // Sort heartbeats by time ascending to easily find the active user at any given time
        const sortedHeartbeats = parsedHeartbeats.sort((a: any, b: any) => a.timeValue - b.timeValue);

        // Group heartbeats by machine id for faster lookup
        const heartbeatsByMachine: Record<string, any[]> = {};
        for (let i = 0; i < sortedHeartbeats.length; i++) {
          const hb = sortedHeartbeats[i];
          const mId = String(hb.id);
          if (!heartbeatsByMachine[mId]) heartbeatsByMachine[mId] = [];
          heartbeatsByMachine[mId].push(hb);
        }

        // Correlate events with users based on the last heartbeat for that machine
        const mappedEvents = rawEvents.map((event: any) => {
          if (!event.user) {
            const mId = String(event.id);
            const hbs = heartbeatsByMachine[mId];
            if (hbs) {
              const eventTime = event.stamp ? new Date(event.stamp).getTime() : 0;
              let assignedUser = null;
              for (let i = hbs.length - 1; i >= 0; i--) {
                // If heartbeat is before event, and within 15 minutes, we assign it.
                if (hbs[i].timeValue <= eventTime) {
                  if (eventTime - hbs[i].timeValue <= 15 * 60 * 1000) {
                    assignedUser = hbs[i].user;
                  }
                  break;
                }
              }
              // Don't fallback to the first heartbeat if it's outside the time window
              if (!assignedUser && hbs.length > 0) {
                if (Math.abs(hbs[0].timeValue - eventTime) <= 15 * 60 * 1000) {
                  assignedUser = hbs[0].user;
                }
              }
              return { ...event, user: assignedUser };
            }
          }
          return event;
        });

        setHeartbeats(sortedHeartbeats);
        setEvents(mappedEvents);

        const combinedAnalytics: ChecklistAnalytics = {
          total: 0, completed: 0, timeout: 0, blocked: 0, average_duration_minutes: 0,
        };
        let totalDuration = 0;

        checklistResps.forEach((resp: any) => {
          const data = resp.data || {};
          combinedAnalytics.total += (data.total || 0);
          combinedAnalytics.completed += (data.completed || 0);
          combinedAnalytics.timeout += (data.timeout || 0);
          combinedAnalytics.blocked += (data.blocked || 0);
          totalDuration += (data.average_duration_minutes || 0) * (data.completed || 0);
        });

        if (combinedAnalytics.completed > 0) {
          combinedAnalytics.average_duration_minutes = Math.round(totalDuration / combinedAnalytics.completed);
        }

        setChecklistAnalytics(combinedAnalytics);
      })
      .catch((err) => {
        console.error('machine users dashboard telemetry load error', err);
        setError('Não foi possível carregar telemetria/eventos do usuário selecionado.');
      })
      .finally(() => setLoadingTelemetry(false));
  }, [operation, selectedUser, startDate, endDate]);

  return (
    <DrawerHeader title="Dashboard Usuários Máquina">
      <Grid container spacing={2} sx={{ mt: 0, ml: 0 }} direction="column">
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: 'background.paper',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              mb: 1
            }}
          >
            <Box>
              <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Operador Individual</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Analise o perfil de uso, jornada de trabalho, pausas e o risco de eventos de cada operador da frota. Acompanhe a produtividade individual e avalie padrões de condução a partir de dados reais de telemetria.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={5} sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="machine-user-dashboard-user-label">Usuário de máquina</InputLabel>
                  <Select
                    labelId="machine-user-dashboard-user-label"
                    value={selectedUser}
                    label="Usuário de máquina"
                    disabled={loadingUsers || userOptions.length === 0}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedUser(value);
                      localStorage.setItem('selectedMachineDashboardUser', value);
                    }}
                  >
                    {userOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Nome: ${selectedUserRecord?.name || '—'}`} />
                  <Chip label={`Login: ${selectedUser || '—'}`} />
                  <Chip label={`Área: ${selectedUserRecord?.area || '—'}`} />
                  <Chip label={`Tipo: ${selectedUserRecord?.type || '—'}`} />
                  <Chip label={`Máquinas permitidas: ${Array.isArray(selectedUserRecord?.machines) ? selectedUserRecord?.machines.join(', ') : selectedUserRecord?.machines || '—'}`} />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {(loadingUsers || loadingTelemetry) && (
          <Grid item xs={12}>
            <Paper elevation={5} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Carregando análise do usuário...</Typography>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, mt: 1, fontWeight: 'bold' }}>Métricas Operacionais</Typography>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Média por dia trabalhado" value={formatHours(analytics.averageShiftHours)} detail={analytics.workedDays > 0 ? `Horas trabalhadas ÷ ${analytics.workedDays} dia(s) com trabalho. Dias sem trabalho não entram na média.` : "Nenhum dia com trabalho registrado no período."} color="#0891b2" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Batidas" value={analytics.crashCount} detail="Eventos de batida/impacto." color="#dc2626" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Buracos" value={analytics.holeCount} detail="Eventos de buraco/pothole." color="#ea580c" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Máquinas usadas" value={analytics.machinesWorked} detail={`${analytics.machineSwitches} troca(s) detectada(s).`} color="#16a34a" />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Análise de Checklist</Typography>
          <Grid container spacing={2} direction="row" justifyContent="center">
            <Grid item xs={12} sm={6} md>
              <MetricCard title="Total" value={checklistAnalytics?.total || 0} detail="Checklists iniciados no período." color="#475569" />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <MetricCard title="Concluídos" value={checklistAnalytics?.completed || 0} detail="Checklists finalizados com sucesso." color="#10b981" />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <MetricCard title="Média de Tempo" value={`${checklistAnalytics?.average_duration_minutes || 0} min`} detail="Tempo médio gasto para realizar os concluídos." color="#d97706" />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <MetricCard title="Tempo Esgotado" value={checklistAnalytics?.timeout || 0} detail="Checklists que expiraram o tempo limite." color="#f59e0b" />
            </Grid>
            <Grid item xs={12} sm={6} md>
              <MetricCard title="Bloqueados" value={checklistAnalytics?.blocked || 0} detail="Checklists que geraram bloqueio da máquina." color="#ef4444" />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <HeartbeatTimelineChart 
            TITLE="Visão diária" 
            DATA={analytics.timelineRangePoints} 
            startDate={startDate ? dateInputValue(startDate) : undefined}
            endDate={endDate ? dateInputValue(endDate) : undefined}
          />
        </Grid>

        <Grid item xs={12}>
          <MachineUserWeekTable operation={operation} selectedUser={selectedUser} />
        </Grid>

        <Grid item xs={12}>
          <LinePerTimeChart TITLE="Horas trabalhadas por dia (Brasília)" MAXY={24} DATA={workHoursSeries} />
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={5} sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Últimas ocorrências do usuário</Typography>
            <Stack spacing={1.2}>
              {selectedUserEvents.length === 0 && (
                <Typography color="text.secondary">Nenhuma ocorrência encontrada para este usuário no período.</Typography>
              )}
              {selectedUserEvents.slice((eventsPage - 1) * eventsPerPage, eventsPage * eventsPerPage).map((event: any, index) => (
                <Box
                  key={`${event.stamp}-${event.id}-${index}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc'),
                    border: (theme) => (theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0'),
                  }}
                >
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" mb={0.75}>
                    <Chip size="small" color="primary" label={event.type || 'desconhecido'} />
                    {event.severity && <Chip size="small" color="error" label={event.severity} />}
                    {event.category && <Chip size="small" label={event.category} />}
                    {event.subcategory && <Chip size="small" label={event.subcategory} />}
                  </Stack>
                  <Typography fontWeight={700}>{formatTimestamp(event.stamp)} · Máquina {String(event.id ?? '—')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {event.description || event.reason || (event.type === 'hole' ? 'Buraco detectado' : event.type === 'crash' ? 'Batida detectada' : 'Sem descrição')}
                  </Typography>

                  {/* Render additional fields dynamically */}
                  <Box mt={1}>
                    {Object.entries(event).map(([key, val]) => {
                      if (!['acc_h', 'acc_v', 'ax', 'ay', 'jerk_h', 'svm', 'lat', 'lon'].includes(key)) return null;
                      if (val === null || val === undefined) return null;
                      return <Chip key={key} size="small" variant="outlined" label={`${key}: ${typeof val === 'number' ? (Math.round(val * 100) / 100) : String(val)}`} sx={{ mr: 0.5, mb: 0.5, fontSize: 10, height: 20 }} />
                    })}
                  </Box>
                </Box>
              ))}

              {selectedUserEvents.length > eventsPerPage && (
                <Box display="flex" justifyContent="center" mt={2} mb={1}>
                  <Pagination
                    count={Math.ceil(selectedUserEvents.length / eventsPerPage)}
                    page={eventsPage}
                    onChange={(_, val) => setEventsPage(val)}
                    color="primary"
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </DrawerHeader>
  );
}
